import { useState } from 'react'
import { Check } from 'lucide-react'
import { ENV } from '@shared/config/env'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import { useProfileQuery } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import { LinkEmailModal } from '@features/link-email/ui/LinkEmailModal'
import { LinkTelegramWaitModal } from '@features/link-telegram/ui/LinkTelegramWaitModal'
import { generateLinkTelegram } from '@features/link-telegram/api/linkTelegramApi'
import styles from './ProfileLoginMethods.module.css'

/**
 * Блок «Способы входа» в профиле: показывает привязанные email и telegram,
 * а для непривязанных способов — кнопку «Привязать» (в зависимости от сборки tg/web).
 */
export const ProfileLoginMethods = () => {
  const platform = usePlatform()
  const profileFromStore = useAppStore((s) => s.profile)
  const { data } = useProfileQuery(true)
  const profile = data ?? profileFromStore
  const [linkEmailOpen, setLinkEmailOpen] = useState(false)
  const [waitTgOpen, setWaitTgOpen] = useState(false)
  const [tgError, setTgError] = useState<string | null>(null)

  const handleLinkTelegram = async () => {
    setTgError(null)
    // Окно открываем синхронно — иначе браузер блокирует popup после await.
    const tab = prepareAsyncOpenTab(platform)
    try {
      const { link } = await generateLinkTelegram()
      tab.complete(link)
      setWaitTgOpen(true)
    } catch {
      tab.abort()
      setTgError('Не удалось сгенерировать ссылку. Попробуйте позже.')
    }
  }

  const telegramLabel = profile?.telegram
    ? profile.telegram.username
      ? `@${profile.telegram.username}`
      : [profile.telegram.first_name, profile.telegram.last_name].filter(Boolean).join(' ')
    : null

  const emailLinked = Boolean(profile?.email)
  const tgLinked = Boolean(profile?.telegram)
  const showEmailLinkButton = !emailLinked && ENV.IS_TG
  const showTgLinkButton = !tgLinked && ENV.IS_WEB

  return (
    <section className={styles.section}>
      <h3 className={styles.title}>Способы входа</h3>

      <div className={`${styles.row} ${showEmailLinkButton ? styles.rowUnlinked : ''}`}>
        <div className={styles.info}>
          <div className={styles.labelRow}>
            <span className={styles.label}>Email</span>
            {emailLinked && <Check className={styles.check} size={20} strokeWidth={2.5} aria-hidden="true" />}
          </div>
          {emailLinked && <span className={styles.value}>{profile?.email?.email}</span>}
        </div>
        {showEmailLinkButton && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setLinkEmailOpen(true)}
          >
            Привязать
          </button>
        )}
      </div>

      <div className={`${styles.row} ${showTgLinkButton ? styles.rowUnlinked : ''}`}>
        <div className={styles.info}>
          <div className={styles.labelRow}>
            <span className={styles.label}>Telegram</span>
            {tgLinked && <Check className={styles.check} size={20} strokeWidth={2.5} aria-hidden="true" />}
          </div>
          {tgLinked && telegramLabel && <span className={styles.value}>{telegramLabel}</span>}
        </div>
        {showTgLinkButton && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleLinkTelegram}
          >
            Привязать
          </button>
        )}
      </div>

      {tgError && <div className={styles.error}>{tgError}</div>}

      {linkEmailOpen && <LinkEmailModal onClose={() => setLinkEmailOpen(false)} />}
      {waitTgOpen && <LinkTelegramWaitModal onClose={() => setWaitTgOpen(false)} />}
    </section>
  )
}
